<template>
  <V2MoleculesVerticalFormWidgetSidebar
    :data-testid="dataTestid"
    :vertical="vertical"
    :is-income-protection="isIncomeProtection"
    :title-class="
      isIncomeProtection
        ? 'text-lg leading-6 lg:text-base'
        : ''
    "
  />

  <V2MoleculesAgeCta
    v-if="vertical === VERTICAL.HEALTH_INSURANCE"
    title="
      <span class='flex flex-col xxs:flex-row items-center justify-center gap-2 xxs:gap-4'>
        <span class='w-[120px] font-extrabold text-2xl leading-6'>AVERAGE SAVINGS</span>
        <span class='text-lg w-[28px] leading-5'>OF</span>
        <span class='font-extrabold text-5xl'>$300*</span>
      </span>
    "
    disclaimer="*Average savings based on 136,746 customers between 1 Jan 2018 - 23 December 2022"
    theme="dark"
    sub-title="Select your age & compare now!"
  />

  <template v-if="vertical === VERTICAL.LIFE_INSURANCE">
    <V2MoleculesLifeCover
      v-if="!isIncomeProtection"
      title="
        How much <strong class='font-extrabold'>life cover</strong> do you need?
      "
      theme="dark"
      sub-title="We’ll show best quotes for you"
    />

    <V2MoleculesLifeCover
      v-else
      title="
        How much <strong class='font-extrabold'>Income Protection</strong> do you need?
      "
      title-class="text-[1.75rem] leading-[2.25rem] lg:text-2xl"
      theme="dark"
      sub-title="We’ll show best quotes for you"
      :options="[
        '< $50K',
        '$50K - $100K',
        '$100K - $150K',
        '$150K - $200K',
        '$200K - $250K',
        '$250K - $300K',
        '$350K - $400K',
        '$450K - $500K',
        '$500K - $550K',
        '$550K - $600K',
        '$600K - $650K',
        '$650K - $700K',
        '$$700K - $750K',
        '$750K - $800K',
        '$800K - $850K',
        '$850K - $900K',
        '$900 - $950K',
        '$950K - $1M'
      ]"
      options-class="!grid-cols-2"
      :is-income-protection="isIncomeProtection"
      :custom-component="{
        to: '/life-insurance/income-protection/quote/'
      }"
    />
  </template>

  <V2MoleculesLifeCover
    v-if="vertical === VERTICAL.HOME_LOANS"
    :component-vertical="VERTICAL.HOME_LOANS"
    title="
      Select your <strong class='font-extrabold'>state and compare</strong> now!
    "
    theme="dark"
    sub-title="Discover your better value option"
    :options="[
      'NSW',
      'VIC',
      'QLD',
      'SA',
      'WA',
      'ACT',
      'TAS',
      'NT'
    ]"
  />

  <V2MoleculesLifeCover
    v-if="vertical === VERTICAL.ENERGY"
    :component-vertical="VERTICAL.ENERGY"
    title="
      Select your <strong class='font-extrabold'>state and compare</strong> now!
    "
    theme="dark"
    sub-title="Find the right option for you"
    :options="[
      'NSW',
      'VIC',
      'QLD',
      'SA',
      'WA',
      'ACT',
      'TAS',
      'NT'
    ]"
    options-class="!grid-cols-2"
  />

  <V2MoleculesLifeCover
    v-if="vertical === VERTICAL.PERSONAL_LOANS"
    :component-vertical="VERTICAL.PERSONAL_LOANS"
    title="
      <strong class='font-extrabold'>Select your amount and compare now</strong>
    "
    title-class="!text-2xl lg:!text-[2rem] lg:!leading-10"
    theme="dark"
    sub-title="Find a low repayment rate"
    :options="[
      '$2K - $5K',
      '$5K - $10K',
      '$10K - $20K',
      '$20K - $30K',
      '$30K - $40K',
      '$40K - $50K'
    ]"
    options-class="!grid-cols-2"
  />

  <V2MoleculesLifeCover
    v-if="vertical === VERTICAL.CAR_LOANS"
    :component-vertical="VERTICAL.CAR_LOANS"
    title="
      <strong class='font-extrabold'>Approximate<br class='hidden lg:inline-block' /> purchase price?</strong>
    "
    title-class="!text-2xl lg:!text-[2rem] lg:!leading-10"
    theme="dark"
    sub-title="We’ll show best quotes for you"
    subtitle-class="text-lg leading-[1.625rem]"
    :options="[
      'Under $60K',
      '$60K - $70K',
      '$70K - $80K',
      '$80K - $90K',
      '$90K - $100K',
      '$100K - $110K',
      '$110K - $120K',
      '$120K - $130K',
      '$130K - $140K',
      '$140K - $150K',
      '$150K - $160K',
      '$160K - $170K',
      '$170K - $180K',
      '$180K - $190K',
      '$190K - $200K',
      'Above $200K'
    ]"
    options-class="!grid-cols-2"
  />

  <V2MoleculesLifeCover
    v-if="vertical === VERTICAL.CREDIT_CARDS"
    :component-vertical="VERTICAL.CREDIT_CARDS"
    title="
      Quick and easy credit <br /><strong class='font-extrabold'>card comparison</strong>
    "
    title-class="!text-2xl lg:!text-[2rem] lg:!leading-10"
    theme="dark"
    sub-title="I'm looking for:"
    subtitle-class="text-lg leading-[1.625rem]"
    options-class="!grid-cols-1 gap-y-3"
    cta-class="!justify-start px-4 py-3 gap-4"
    :options="[
      {
        label: 'Premium Credit Cards',
        icon: '/icons/credit-cards/premium-credit-card.svg',
        pageUrl: '/credit-cards/premium-cards/',
        iconDimensions: {
          width: 24,
          height: 24
        }
      },
      {
        label: 'Low Fees Cards',
        icon: '/images/menu/credit-cards/low-fee-cards.svg',
        pageUrl: '/credit-cards/low-fee-cards/',
        iconDimensions: {
          width: 24,
          height: 24
        }
      },
      {
        label: 'Balance Transfer Cards',
        icon: '/images/menu/credit-cards/balance-transfer-cards.svg',
        pageUrl: '/credit-cards/balance-transfer-cards/',
        iconDimensions: {
          width: 24,
          height: 24
        }
      },
      {
        label: 'Cash Back Cards',
        icon: '/images/menu/credit-cards/cashback-cards.svg',
        pageUrl: '/credit-cards/cash-back-cards/',
        iconDimensions: {
          width: 24,
          height: 24
        }
      },
      {
        label: 'Rewards Cards',
        icon: '/images/menu/credit-cards/rewards-cards.svg',
        pageUrl: '/credit-cards/rewards-cards/',
        iconDimensions: {
          width: 24,
          height: 24
        }
      },
      {
        label: 'Frequent Flyer Cards',
        icon: '/images/menu/credit-cards/frequent-flyer-cards.svg',
        pageUrl: '/credit-cards/frequent-flyer-cards/',
        iconDimensions: {
          width: 24,
          height: 24
        }
      }
    ]"
  />
</template>

<script setup>
defineOptions({
  name: 'V2OrganismContentWidgetsSidebarV2'
})

defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  vertical: {
    type: String,
    default: ''
  },

  isIncomeProtection: {
    type: Boolean,
    default: false
  }
})
</script>
